import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'code-galaxy-progress';
const TOTAL_ITEMS = 75;

const defaultProgress = {
  completedItems: [],
  totalXP: 0,
  badges: [],
};

const ProgressContext = createContext(null);

export function ProgressProvider({ children }) {
  const [progress, setProgress] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultProgress;
    } catch {
      return defaultProgress;
    }
  });

  const [showCelebration, setShowCelebration] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [progress]);

  const completeItem = useCallback((itemId, xp) => {
    setProgress(prev => {
      if (prev.completedItems.includes(itemId)) return prev;
      const newCompleted = [...prev.completedItems, itemId];
      const newXP = prev.totalXP + xp;
      const pct = (newCompleted.length / TOTAL_ITEMS) * 100;
      const newBadges = [...prev.badges];
      if (pct >= 25 && !newBadges.includes('explorer')) newBadges.push('explorer');
      if (pct >= 50 && !newBadges.includes('builder')) newBadges.push('builder');
      if (pct >= 75 && !newBadges.includes('creator')) newBadges.push('creator');
      if (pct >= 100 && !newBadges.includes('master')) newBadges.push('master');
      return { completedItems: newCompleted, totalXP: newXP, badges: newBadges };
    });
    setShowCelebration({ itemId, xp });
  }, []);

  const isUnlocked = useCallback((itemId) => {
    if (itemId === 1) return true;
    return progress.completedItems.includes(itemId - 1);
  }, [progress.completedItems]);

  const isCompleted = useCallback((itemId) => {
    return progress.completedItems.includes(itemId);
  }, [progress.completedItems]);

  const resetProgress = useCallback(() => {
    setProgress(defaultProgress);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const dismissCelebration = useCallback(() => {
    setShowCelebration(null);
  }, []);

  return (
    <ProgressContext.Provider value={{
      progress, completeItem, isUnlocked, isCompleted,
      resetProgress, showCelebration, dismissCelebration,
      totalItems: TOTAL_ITEMS,
    }}>
      {children}
    </ProgressContext.Provider>
  );
}

export const useProgress = () => {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used within ProgressProvider');
  return ctx;
};
